'use client';

import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	Grid,
	MenuItem,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { gql, useLazyQuery, useMutation } from '@parkchirp/api-client';
import type { Dayjs } from 'dayjs';
import { useCallback, useState } from 'react';
import { GET_PARKER_FOR_PARKER_DETAIL } from '../../../queries';
import type { SetShowSnackbar } from '../../../types';
import { dayjs, isTenantPortal } from '../../../utils';
import { getHistoryErrorMessage, getMinDate, shouldDisableDate } from './shared';

type RateHistoryModalProps = {
	parkerId: string;
	rateHistoryId: number;
	rateHistory: { id: number; startDate: string }[];
	parkerHasBeenInvoiced: (date: string) => boolean;
	closeModal: () => Promise<void>;
	setShowSnackbar: SetShowSnackbar;
	tenantSubscriptionId?: string;
	refetchParkersFromCache?: () => void;
	fetchParkerSublotHistory?: () => void;
	sublotHistory: any[];
	updateParkerSublotHistory: (args) => void;
};

const GET_PARKER_RATE_OPTIONS = gql`
    query getParkerRateOptions(
        $parkerId: String!,
        $date: String!,
        $tenantSubscriptionId: String
    ) {
        getParkerRateOptions(
            parkerId: $parkerId,
            date: $date,
            tenantSubscriptionId: $tenantSubscriptionId
        ) {
            id,
            name,
            displayPrice,
            rateCodeName,
            productCodeName,
            tenantRateSublotSelections {
                sublot {
                    id,
                    name
                    primaryLot
                }
            }
        }
    }
`;

const CREATE_PARKER_RATE_HISTORY = gql`
    mutation createParkerRateHistory(
        $parkerId: String!,
        $rateId: String!,
        $startDate: String!,
        $tenantSubscriptionId: String
    ) {
        createParkerRateHistory(
            parkerId: $parkerId,
            rateId: $rateId,
            startDate: $startDate,
            tenantSubscriptionId: $tenantSubscriptionId
        )
    }
`;

const UPDATE_PARKER_RATE_HISTORY = gql`
    mutation updateParkerRateHistory(
        $parkerRateHistoryId: Int!,
        $rateId: String!,
        $startDate: String!,
        $tenantSubscriptionId: String
    ) {
        updateParkerRateHistory(
            parkerRateHistoryId: $parkerRateHistoryId,
            rateId: $rateId,
            startDate: $startDate,
            tenantSubscriptionId: $tenantSubscriptionId
        )
    }
`;

const CREATE_PARKER_SUBLOT_HISTORY = gql`
    mutation createParkerSublotHistory(
        $parkerId: String!,
        $sublotId: Int!,
        $startDate: String!,
        $tenantSubscriptionId: String
    ) {
        createParkerSublotHistory(
            parkerId: $parkerId,
            sublotId: $sublotId,
            startDate: $startDate,
            tenantSubscriptionId: $tenantSubscriptionId
        )
    }
`;

const steps = ['Select a date', 'Select a rate', ...(isTenantPortal ? ['Select a sublot'] : [])];
export const EditRateHistoryModal = ({
	parkerId,
	rateHistoryId,
	rateHistory,
	parkerHasBeenInvoiced,
	closeModal,
	setShowSnackbar,
	tenantSubscriptionId,
	refetchParkersFromCache,
	fetchParkerSublotHistory,
	sublotHistory,
	updateParkerSublotHistory,
}: RateHistoryModalProps) => {
	const editMode = !!rateHistoryId;
	const editingStartDateRecord = rateHistoryId === rateHistory[0].id;
	const billingStartDate = dayjs.utc(rateHistory[0].startDate);
	const selectedRateHistory = rateHistory.find(({ id }) => id === rateHistoryId);

	const [activeStep, setActiveStep] = useState<number>(0);
	const [startDate, setStartDate] = useState<string>(selectedRateHistory?.startDate ?? dayjs.utc().format());
	const [rateId, setRateId] = useState<string>('');
	const [sublotId, setSublotId] = useState<number>();
	const [rateOptions, setRateOptions] = useState<{ value: string; label: string }[]>([]);
	const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
	const [getParkerRateOptions, { loading: rateOptionsLoading }] = useLazyQuery(GET_PARKER_RATE_OPTIONS);
	const [createParkerRateHistory, { loading: createInProgress }] = useMutation(CREATE_PARKER_RATE_HISTORY);
	const [updateParkerRateHistory, { loading: updateInProgress }] = useMutation(UPDATE_PARKER_RATE_HISTORY);
	const [rateIdToSublotsMap, setRateIdToSublotsMap] = useState(new Map<string, any[]>());
	const [createParkerSublotHistory, { loading: createSublotInProgress }] = useMutation(CREATE_PARKER_SUBLOT_HISTORY);

	const fetchParkerRateOptions = useCallback(async () => {
		const { data } = await getParkerRateOptions({
			variables: {
				parkerId,
				date: startDate,
				tenantSubscriptionId,
			},
			context: { apiVersion: 'v2' },
			fetchPolicy: 'network-only',
		});

		const formattedRateOptions = data.getParkerRateOptions
			.map((rateOption) => {
				const { id, name, displayPrice, rateCodeName, productCodeName, tenantRateSublotSelections } =
					rateOption;
				if (isTenantPortal) {
					setRateIdToSublotsMap((prevMap) => {
						const newMap = new Map(prevMap);
						const sublots = tenantRateSublotSelections.map((sel) => sel.sublot);
						newMap.set(id, sublots);
						return newMap;
					});
				}
				const productCode = `${productCodeName ? ` ${productCodeName}` : ''}`;
				const rateCode = `${rateCodeName ? ` ${rateCodeName}` : ''}`;
				const productAndRateCode = `${productCodeName || rateCodeName ? ' -' : ''}${productCode}${rateCode}`;

				return {
					value: id,
					label: `${name} ($${displayPrice})${productAndRateCode}`,
				};
			})
			.flat();

		setRateOptions(formattedRateOptions);
	}, [getParkerRateOptions, parkerId, startDate, tenantSubscriptionId]);

	const handleBack = useCallback(() => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
		setRateId('');
	}, []);

	const handleNext = useCallback(async () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);

		await fetchParkerRateOptions();
	}, [fetchParkerRateOptions]);

	const saveRateHistory = useCallback(async () => {
		try {
			if (editMode) {
				await updateParkerRateHistory({
					variables: {
						parkerRateHistoryId: rateHistoryId,
						rateId,
						startDate,
						tenantSubscriptionId,
					},
					context: { apiVersion: 'v2' },
					refetchQueries: [
						{
							query: GET_PARKER_FOR_PARKER_DETAIL,
							variables: {
								parkerId,
								tenantSubscriptionId,
							},
						},
						'getParker',
					],
				});
				if (isTenantPortal) {
					const { startDate: startDateFromRateHistory } = rateHistory.find(
						(rate) => rate.id === rateHistoryId,
					);
					const correspondingSublot = sublotHistory.find(
						(sublot) => sublot.startDate === startDateFromRateHistory,
					);
					if (!correspondingSublot) return;
					await updateParkerSublotHistory({
						variables: {
							parkerSublotHistoryId: correspondingSublot.id,
							sublotId,
							startDate,
							tenantSubscriptionId,
						},
						context: { apiVersion: 'v2' },
						refetchQueries: [
							{
								query: GET_PARKER_FOR_PARKER_DETAIL,
								variables: {
									parkerId,
									tenantSubscriptionId,
								},
							},
							'getParker',
						],
					});
				}
			} else {
				await createParkerRateHistory({
					variables: {
						parkerId,
						rateId,
						startDate,
						tenantSubscriptionId,
					},
					context: { apiVersion: 'v2' },
					refetchQueries: [
						{
							query: GET_PARKER_FOR_PARKER_DETAIL,
							variables: {
								parkerId,
								tenantSubscriptionId,
							},
						},
						'getParker',
					],
				});
				if (isTenantPortal) {
					await createParkerSublotHistory({
						variables: {
							parkerId,
							sublotId,
							startDate,
							tenantSubscriptionId,
						},
						context: { apiVersion: 'v2' },
						refetchQueries: [
							{
								query: GET_PARKER_FOR_PARKER_DETAIL,
								variables: {
									parkerId,
									tenantSubscriptionId,
								},
							},
							'getParker',
						],
					});
				}
			}
			await fetchParkerSublotHistory();
			if (refetchParkersFromCache) {
				await refetchParkersFromCache();
			}
			await closeModal();
		} catch (err) {
			setShowSnackbar({
				message: getHistoryErrorMessage(err, 'Failed to save rate change'),
				severity: 'error',
				autoHideDuration: 6000,
			});
		}
	}, [
		closeModal,
		createParkerRateHistory,
		editMode,
		parkerId,
		rateHistoryId,
		rateId,
		setShowSnackbar,
		startDate,
		updateParkerRateHistory,
		tenantSubscriptionId,
		sublotId,
		createParkerSublotHistory,
		updateParkerSublotHistory,
		refetchParkersFromCache,
		fetchParkerSublotHistory,
		rateHistory,
		sublotHistory,
	]);

	const handleSave = useCallback(async () => {
		if (parkerHasBeenInvoiced(startDate)) {
			setShowConfirmationDialog(true);
		} else {
			await saveRateHistory();
		}
	}, [parkerHasBeenInvoiced, saveRateHistory, startDate]);

	const handleConfirm = useCallback(async () => {
		setShowConfirmationDialog(false);
		await saveRateHistory();
	}, [saveRateHistory]);

	const minDate = getMinDate(billingStartDate, editingStartDateRecord);

	const nextDisabled =
		dayjs.utc(startDate).isBefore(minDate, 'day') ||
		shouldDisableDate(rateHistory, rateHistoryId, dayjs.utc(startDate));

	const loading = rateOptionsLoading || createInProgress || updateInProgress || createSublotInProgress;

	const confirmationDialog = (
		<Dialog open={showConfirmationDialog}>
			<Grid item textAlign='center' p={3}>
				<Typography>
					An invoice has been created for this parking period so it should be manually adjusted. Are you sure
					you want to continue?
				</Typography>
				<Grid container justifyContent='space-between' pt={3}>
					<Button onClick={() => setShowConfirmationDialog(false)}>Cancel</Button>
					<Button onClick={handleConfirm}>Confirm</Button>
				</Grid>
			</Grid>
		</Dialog>
	);

	const rateHistoryToDisableDates = isTenantPortal ? [...rateHistory, ...sublotHistory] : rateHistory;

	return showConfirmationDialog ? (
		confirmationDialog
	) : (
		<Grid container direction='column' rowSpacing={3}>
			<Grid item>
				<Stepper activeStep={activeStep}>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
			</Grid>
			{activeStep === 0 && (
				<Grid item textAlign='center'>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker<Dayjs>
							value={dayjs.utc(startDate)}
							minDate={minDate}
							shouldDisableDate={(date) =>
								shouldDisableDate(rateHistoryToDisableDates, rateHistoryId, date)
							}
							disabled={editingStartDateRecord}
							onChange={(value) => setStartDate(value.format())}
							sx={{ width: '60%', outlineStyle: 'none' }}
							slotProps={{
								textField: {
									inputProps: {
										'data-cy': 'rateHistory-startDate',
										disabled: true,
									},
									variant: 'standard',
								},
							}}
						/>
					</LocalizationProvider>
					<Grid container justifyContent='space-between' pt={3}>
						<Button onClick={closeModal}>Cancel</Button>
						<Button onClick={handleNext} disabled={nextDisabled}>
							Next
						</Button>
					</Grid>
				</Grid>
			)}
			{activeStep === 1 &&
				(loading ? (
					<Grid textAlign='center' pt={4}>
						<CircularProgress />
					</Grid>
				) : (
					<Grid item textAlign='center'>
						<TextField
							select
							value={rateId}
							onChange={(e) => setRateId(e.target.value)}
							style={{ width: '100%' }}
						>
							{rateOptions?.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>
						<Grid container justifyContent='space-between' pt={3}>
							<Box>
								<Button onClick={handleBack}>Back</Button>
							</Box>
							<Box>
								{isTenantPortal ? (
									<Button onClick={handleNext} disabled={!rateId}>
										Next
									</Button>
								) : (
									<Button onClick={handleSave} disabled={!rateId}>
										Save
									</Button>
								)}
							</Box>
						</Grid>
					</Grid>
				))}
			{activeStep === 2 &&
				(loading ? (
					<Grid textAlign='center' pt={4}>
						<CircularProgress />
					</Grid>
				) : (
					<Grid item textAlign='center'>
						<TextField
							select
							value={sublotId}
							onChange={(e) => setSublotId(Number(e.target.value))}
							style={{ width: '100%' }}
						>
							{(rateIdToSublotsMap.get(rateId) ?? []).length > 0 ? (
								(rateIdToSublotsMap.get(rateId) ?? []).map((option) => (
									<MenuItem key={option.id} value={option.id}>
										{option.name}
									</MenuItem>
								))
							) : (
								<MenuItem disabled>No available sublots</MenuItem>
							)}
						</TextField>
						<Grid container justifyContent='space-between' pt={3}>
							<Box>
								<Button onClick={handleBack}>Back</Button>
							</Box>
							<Box>
								<Button onClick={handleSave} disabled={!sublotId}>
									Save
								</Button>
							</Box>
						</Grid>
					</Grid>
				))}
		</Grid>
	);
};
